import { useEffect, useState } from 'react';
import { authHeaders } from '../listPageStyles';
import { RelationPicker } from '../ui';
import { RELATION_CONFIGS } from '../relationConfigs';
import { unwrapLookupItems } from '../c360';
import { lookupLabel } from './campaignUtils';
import {
  SubModal, Section, Grid, Icon,
  FieldText, FieldSelect, FieldCustom,
} from '../detail/DetailShell';

// Kampanya "Müşteriler" sekmesinden, ekrandan ayrılmadan yeni bir Müşteri
// Özelleştirilmiş Grup (CustomerDynamicGroup) oluşturmak için hızlı-ekle modalı.
// CustomerDynamicGroupDetailPage'in çekirdek akışının (Kod/Açıklama + kriter kuralları +
// havuz önizleme) sadeleştirilmiş kopyasıdır; aynı SaveWithDetails/ResolveCustomers
// endpoint'lerini kullanır. Kaydettiğinde onCreated({ value, label }) döner ki çağıran
// yeni grubu doğrudan kampanya hedefine (chip) ekleyebilsin.
const API_BASE = '/api/customerdynamicgroups';

const TYPE_GROUP = 1;
const TYPE_SUBGROUP = 2;
const TYPE_CODE = 3;
const TYPE_OPTIONS = [
  { value: TYPE_GROUP, label: 'Grup' },
  { value: TYPE_SUBGROUP, label: 'Ek Grup' },
  { value: TYPE_CODE, label: 'Kod' },
];
const TYPE_LABEL = { [TYPE_GROUP]: 'Grup', [TYPE_SUBGROUP]: 'Ek Grup', [TYPE_CODE]: 'Kod' };
const EMPTY_CRITERION = { criteriaType: TYPE_GROUP, groupId: '', subGroupId: '', customer: null };

export function CustomerDynamicGroupQuickCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ textId: '', description: '' });
  const [criteria, setCriteria] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [err, setErr] = useState('');
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Kriter ekle/düzenle alt-modalı
  const [criterionModal, setCriterionModal] = useState(null); // { index|null }
  const [criterionForm, setCriterionForm] = useState(EMPTY_CRITERION);

  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const setCrit = (k, v) => setCriterionForm((c) => ({ ...c, [k]: v }));

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/customergroups/Select', { headers }).then((res) => (res.ok ? res.json() : [])),
      fetch('/api/customersubgroups/Select', { headers }).then((res) => (res.ok ? res.json() : [])),
    ]).then(([groupData, subGroupData]) => {
      setGroups(unwrapLookupItems(groupData));
      setSubGroups(unwrapLookupItems(subGroupData));
    }).catch(() => {});
  }, []);

  const openNewCriterion = () => { setErr(''); setCriterionForm(EMPTY_CRITERION); setCriterionModal({ index: null }); };
  const openEditCriterion = (index) => {
    const c = criteria[index];
    setCriterionForm({
      criteriaType: c.criteriaType,
      groupId: c.criteriaType === TYPE_GROUP ? (c.groupId || '') : '',
      subGroupId: c.criteriaType === TYPE_SUBGROUP ? (c.subGroupId || '') : '',
      customer: c.criteriaType === TYPE_CODE && c.customerId ? { id: c.customerId, label: c.label?.replace(/^Kod:\s*/, '') || '' } : null,
    });
    setCriterionModal({ index });
  };

  const handleSaveCriterion = () => {
    const t = Number(criterionForm.criteriaType);
    let criterion = null;
    if (t === TYPE_GROUP) {
      if (!criterionForm.groupId) { setErr('Bir grup seçiniz'); return; }
      const g = groups.find((item) => item.id === criterionForm.groupId);
      criterion = { criteriaType: TYPE_GROUP, groupId: criterionForm.groupId, subGroupId: null, customerId: null, label: `Grup: ${lookupLabel(g)}` };
    } else if (t === TYPE_SUBGROUP) {
      if (!criterionForm.subGroupId) { setErr('Bir ek grup seçiniz'); return; }
      const g = subGroups.find((item) => item.id === criterionForm.subGroupId);
      criterion = { criteriaType: TYPE_SUBGROUP, groupId: null, subGroupId: criterionForm.subGroupId, customerId: null, label: `Ek Grup: ${lookupLabel(g)}` };
    } else {
      if (!criterionForm.customer?.id) { setErr('Bir müşteri seçiniz'); return; }
      criterion = { criteriaType: TYPE_CODE, groupId: null, subGroupId: null, customerId: criterionForm.customer.id, label: `Kod: ${criterionForm.customer.label}` };
    }

    const editIndex = criterionModal?.index;
    const dup = criteria.some((c, i) => i !== editIndex
      && c.criteriaType === criterion.criteriaType && c.groupId === criterion.groupId
      && c.subGroupId === criterion.subGroupId && c.customerId === criterion.customerId);
    if (dup) { setErr('Bu kriter zaten ekli'); return; }

    setCriteria((current) => {
      if (editIndex === null || editIndex === undefined) return [...current, criterion];
      const next = [...current];
      next[editIndex] = criterion;
      return next;
    });
    setPreviewCount(null);
    setErr('');
    setCriterionModal(null);
  };

  const removeCriterion = (index) => {
    setCriteria((current) => current.filter((_, i) => i !== index));
    setPreviewCount(null);
  };

  const buildDetailPayload = () => criteria.map((c) => ({
    id: '00000000-0000-0000-0000-000000000000',
    criteriaType: c.criteriaType,
    groupId: c.groupId,
    subGroupId: c.subGroupId,
    customerId: c.customerId,
  }));

  const previewPool = async () => {
    if (criteria.length === 0) { setErr('Önce kriter ekleyin'); return; }
    setPreviewLoading(true);
    setErr('');
    try {
      const res = await fetch(`${API_BASE}/ResolveCustomers`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ details: buildDetailPayload() }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Önizleme başarısız');
      const pool = body?.data ?? body?.Data ?? [];
      setPreviewCount(Array.isArray(pool) ? pool.length : 0);
    } catch (error) {
      setErr(error.message || 'Önizleme sırasında hata oluştu');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    setTried(true);
    if (!form.textId.trim()) { setErr('Kod boş olamaz'); return; }
    if (criteria.length === 0) { setErr('En az bir kriter ekleyin'); return; }
    setSaving(true);
    setErr('');
    try {
      const res = await fetch(`${API_BASE}/SaveWithDetails`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          header: { id: '00000000-0000-0000-0000-000000000000', textId: form.textId.trim(), description: form.description },
          details: buildDetailPayload(),
          deletedDetailIds: [],
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Grup kaydedilemedi');
      const data = body?.data ?? body?.Data ?? {};
      const newId = data.id ?? data.Id ?? data.header?.id ?? data.Header?.Id
        ?? data.headerId ?? data.HeaderId ?? '';
      if (!newId) throw new Error('Kayıt oluşturuldu ancak kimlik alınamadı');
      const label = form.description ? `${form.textId.trim()} - ${form.description}` : form.textId.trim();
      onCreated({ value: newId, label });
    } catch (error) {
      setErr(error.message || 'İşlem sırasında hata oluştu');
      setSaving(false);
    }
  };

  const critType = Number(criterionForm.criteriaType);

  return (
    <>
    <SubModal
      title="Yeni Müşteri Özelleştirilmiş Grup"
      maxWidth={720}
      onClose={onClose}
      footer={(
        <>
          <button className="dps-btn ghost" onClick={onClose} disabled={saving}>İptal</button>
          <button className="dps-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Kaydediliyor…' : 'Kaydet ve Ekle'}
          </button>
        </>
      )}
    >
      <div className="dps-form">
        {err && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 10, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
            {err}
          </div>
        )}

        <Section icon="layers" title="Grup Bilgileri" subtitle="Kod ve açıklama">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="Kod" req mono />
            <FieldText form={form} set={set} k="description" label="Açıklama" />
          </Grid>
        </Section>

        <Section
          icon="list"
          title="Müşteri Havuzu Kuralları"
          subtitle="Grup, ek grup veya kod bazında kriterler"
          right={<button className="dps-blk-add" onClick={openNewCriterion}><Icon name="plus" size={15} /> Kriter Ekle</button>}
        >
          {criteria.length === 0 ? (
            <div className="dps-empty"><Icon name="list" size={30} /><p>Henüz kriter eklenmedi</p><span>“Kriter Ekle” ile ilk kriteri oluşturun.</span></div>
          ) : (
            <table className="dps-table">
              <thead>
                <tr><th>Tür</th><th>Kriter</th><th className="dps-td-actions">İşlem</th></tr>
              </thead>
              <tbody>
                {criteria.map((c, index) => (
                  <tr key={`crit-${index}`}>
                    <td><strong>{TYPE_LABEL[c.criteriaType] || '—'}</strong></td>
                    <td>{c.label || '(kriter)'}</td>
                    <td className="dps-td-actions">
                      <button className="dps-row-btn warn" onClick={() => openEditCriterion(index)}>Düzenle</button>
                      <button className="dps-row-btn danger" onClick={() => removeCriterion(index)}>Sil</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {criteria.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
              <button className="dps-blk-add" onClick={previewPool} disabled={previewLoading}>
                <Icon name="users" size={15} /> {previewLoading ? 'Hesaplanıyor…' : 'Havuzu Önizle'}
              </button>
              {previewCount !== null && (
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary,#374151)' }}>
                  {previewCount} müşteri eşleşiyor
                </span>
              )}
            </div>
          )}
        </Section>
      </div>
    </SubModal>

      {/* Kriter ekle/düzenle alt-modalı — dış modalın kardeşi olarak render edilir
          (iç içe SubModal'de stacking/z-index sorununu önlemek için). */}
      {criterionModal && (
        <SubModal
          title={criterionModal.index === null || criterionModal.index === undefined ? 'Yeni Kriter' : 'Kriteri Düzenle'}
          maxWidth={520}
          onClose={() => setCriterionModal(null)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setCriterionModal(null)}>İptal</button>
              <button className="dps-btn primary" onClick={handleSaveCriterion}>Kaydet</button>
            </>
          )}
        >
          <Grid cols={2}>
            <FieldSelect
              form={criterionForm}
              set={(k, v) => { setCrit(k, Number(v)); setCrit('groupId', ''); setCrit('subGroupId', ''); setCrit('customer', null); }}
              k="criteriaType"
              label="Tür"
              options={TYPE_OPTIONS}
            />
            {critType === TYPE_GROUP && (
              <FieldCustom label="Müşteri Grubu" req>
                <RelationPicker
                  config={RELATION_CONFIGS.customerGroup}
                  rows={groups}
                  value={criterionForm.groupId}
                  selectedLabel={criterionForm.groupId ? lookupLabel(groups.find((g) => g.id === criterionForm.groupId)) : ''}
                  onChange={(val) => setCrit('groupId', val)}
                />
              </FieldCustom>
            )}
            {critType === TYPE_SUBGROUP && (
              <FieldCustom label="Müşteri Ek Grubu" req>
                <RelationPicker
                  config={RELATION_CONFIGS.customerSubGroup}
                  rows={subGroups}
                  value={criterionForm.subGroupId}
                  selectedLabel={criterionForm.subGroupId ? lookupLabel(subGroups.find((g) => g.id === criterionForm.subGroupId)) : ''}
                  onChange={(val) => setCrit('subGroupId', val)}
                />
              </FieldCustom>
            )}
            {critType === TYPE_CODE && (
              <FieldCustom label="Müşteri (kod/ad ara)" req>
                <RelationPicker
                  config={RELATION_CONFIGS.customer}
                  value={criterionForm.customer?.id || ''}
                  selectedLabel={criterionForm.customer?.label}
                  onChange={(val, item) => setCrit('customer', val ? { id: val, label: item ? lookupLabel(item) : '' } : null)}
                  placeholder="Müşteri kodu veya adı ile ara…"
                />
              </FieldCustom>
            )}
          </Grid>
        </SubModal>
      )}
    </>
  );
}

export default CustomerDynamicGroupQuickCreateModal;
